/**
 * ServerConfigEditor — 서버 구성 편집기(공용 컴포넌트).
 *
 * 틀(ChassisFrame)로 칸을 그리고, 부품 후보를 칸에 끌어 놓거나 클릭해 놓는다. 그림 보기(앞면 · 뒷면 · 보드) · 표 보기(종류 × 수량).
 * 서버 등록(처음 채우기) · 구성 페이지(A6) · 견적 원페이지 서버 구성(C2)이 같이 쓴다 — 이 저장소 컴포넌트 꼴(SolaView, 자기 상태).
 * 근거: docs/VOCABULARY.md "재고 등록 — 서버 한 대 목업", hcltech-parts docs/INVENTORY_SCREENS.md §A1-서버.
 *
 * Emit:
 *  - config:change  { installed, included }
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import type * as api from '../api/client';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/escape-html';

// ─── 어휘 — 칸 · 장착된 · 같이 든 것 · 배치 · 어디서 고르나 ─────────────
export const SLOT_TYPES = ['board', 'cpu', 'dimm', 'bay', 'pcie', 'ocp', 'psu', 'controller'] as const;
export type  SlotType   = typeof SLOT_TYPES[number];
export type  SlotKey    = `${SlotType}:${string}`;            // 'dimm:A3' · 'bay:Front-Box1-3' · 'pcie:Primary-2'

export interface Installed { slot: SlotKey; part: api.PartCandidate }     // 칸에 장착된 것 — 한 장
export interface Included  { part: api.PartCandidate; qty: number }       // 기본 포함(자리 없는 것) — 레일킷 · 케이블 · 케이지 킷, 수량. 자리가 있는 부품(CPU …)은 여기가 아니라 칸에(현민 2026-10-05)

export const CONFIG_LAYOUTS = ['figure', 'table'] as const;
export type  ConfigLayout   = typeof CONFIG_LAYOUTS[number];

export interface ServerConfigEditorData {
  frame:        api.ChassisFrame;
  installed:    Installed[];
  included:     Included[];
  layout:       ConfigLayout;
  selectedSlot: SlotKey | null;
  query:        string;                    // 트레이 검색 — 한 목록(📷 읽은 것 · 재고 · 새 unit)을 치는 대로 거른다
  parts:        api.PartCandidate[];       // 고르기 목록 전체(탭 · 검색은 그릴 때 거른다)
  photoParts:   api.PartCandidate[];       // 📷 사진이 읽은 부품 — 라벨 한 장이 한 장. 전부 배치의 재료. 없으면 탭이 안 보인다
  baseParts:    api.PartCandidate[];       // SKU(BTO)가 기본으로 들고 오는 부품 — 칸에 앉은 채 「기본 포함」 칩. CTO 면 []
}

/** 틀에서 펼친 칸 하나. closed = 자리는 있지만 아직 안 열림(옵션 케이지 · 옵션 라이저 · CPU 수 부족) */
interface Slot { key: SlotKey; type: SlotType; label: string; group: string; closed: boolean; closedWhy: string; bayType: api.EBayType | null }

const SLOT_LABEL: Record<SlotType, string> = { board: '보드', cpu: 'CPU', dimm: 'DIMM', bay: '베이', pcie: 'PCIe', ocp: 'OCP', psu: 'PSU', controller: '컨트롤러' };
const DIMM_BANKS = ['A', 'B', 'C', 'D'] as const;

// ─── 틀 → 칸 ────────────────────────────────────────────────────
/** 틀을 칸으로 펼친다. 옵션 자리는 여는 부품(케이지 킷 · 라이저)이 installed/included 에 있어야 열린다 */
export function slotsOf(data: ServerConfigEditorData): Slot[] {
  const { frame } = data;
  const present = new Set<string>([...data.installed.map((i) => i.part.option_part), ...data.included.map((i) => i.part.option_part)]);
  const cpuCount = data.installed.filter((i) => i.slot.startsWith('cpu:')).length;
  const slots: Slot[] = [];

  slots.push({ key: 'board:1', type: 'board', label: '메인보드', group: 'CPU', closed: false, closedWhy: '', bayType: null });
  for (let n = 1; n <= frame.cpu_sockets; n++) slots.push({ key: `cpu:${n}`, type: 'cpu', label: `CPU${n}`, group: 'CPU', closed: false, closedWhy: '', bayType: null });
  for (let s = 0; s < frame.cpu_sockets; s++) {
    const bank = DIMM_BANKS[s];
    assert(bank !== undefined, `DIMM 뱅크 글자는 소켓 ${frame.cpu_sockets}개까지만 — 소켓 ${s + 1} 의 글자가 없다`);
    const closed = s + 1 > Math.max(cpuCount, 1);
    for (let n = 1; n <= frame.dimm_per_socket; n++) slots.push({ key: `dimm:${bank}${n}`, type: 'dimm', label: `${bank}${n}`, group: `메모리 DIMM ${bank} (CPU${s + 1})`, closed, closedWhy: closed ? `CPU${s + 1} 이 없다` : '', bayType: null });
  }
  for (const bay of frame.bays) {
    const opened = bay.is_default || bay.enabling_parts.some((pn) => present.has(pn));
    for (let n = 1; n <= bay.count; n++) slots.push({ key: `bay:${bay.position}-${n}`, type: 'bay', label: `${n}`, group: `${bay.position} ${bay.count}×${bay.type}${bay.is_default ? '' : ' (옵션)'}`, closed: !opened, closedWhy: opened ? '' : `케이지 킷 ${bay.enabling_parts.join(' · ')} 필요`, bayType: bay.type });
  }
  for (const p of frame.pcie) {
    const riserOk = p.is_default || (p.riser_part !== null && present.has(p.riser_part));
    const procOk  = p.requires_proc <= Math.max(cpuCount, 1);
    const closed  = !riserOk || !procOk;
    slots.push({ key: `pcie:${p.riser}-${p.number}`, type: 'pcie', label: `슬롯${p.number} ${p.form_factor}`, group: `${p.riser} 라이저${p.is_default ? '' : ' (옵션)'}`, closed, closedWhy: !riserOk ? `라이저 ${p.riser_part ?? ''} 필요` : !procOk ? `CPU ${p.requires_proc}개 필요` : '', bayType: null });
  }
  for (let n = 1; n <= frame.ocp_slots; n++)        slots.push({ key: `ocp:${n}`,        type: 'ocp',        label: `OCP${n}`,  group: '뒷면', closed: false, closedWhy: '', bayType: null });
  for (let n = 1; n <= frame.psu_slots; n++)        slots.push({ key: `psu:${n}`,        type: 'psu',        label: `PSU${n}`,  group: '뒷면', closed: false, closedWhy: '', bayType: null });
  for (let n = 1; n <= frame.controller_slots; n++) slots.push({ key: `controller:${n}`, type: 'controller', label: `컨트롤러${n}`, group: '컨트롤러', closed: false, closedWhy: '', bayType: null });
  return slots;
}

/** 카테고리 → 들어갈 수 있는 칸 종류. 대조표(INVENTORY_SCREENS §A1-서버) 그대로 */
const SLOT_TYPES_OF_CATEGORY: Record<string, SlotType[]> = {
  cpu: ['cpu'], memory: ['dimm'], ssd: ['bay'], hdd: ['bay'], gpu: ['pcie'],
  network_adapter: ['pcie', 'ocp'], raid_controller: ['pcie', 'controller'], hba: ['pcie', 'controller'], power_supply: ['psu'],
};

/** 이 칸이 이 부품을 받나 — 종류 · 베이 꼴(SFF/LFF/NVMe) · 열려 있나 */
export function canPlace(slot: Slot, part: api.PartCandidate): boolean {
  if (slot.closed) return false;
  const types = SLOT_TYPES_OF_CATEGORY[part.category];
  if (types === undefined || !types.includes(slot.type)) return false;
  if (slot.type === 'bay' && slot.bayType !== null) {
    const desc = part.description.toUpperCase();
    const bayForm = slot.bayType === 'NVMe' ? 'NVME' : slot.bayType;
    if (!desc.includes(bayForm)) return false;
  }
  return true;
}

/** 자리 없는 부품인가 — 어느 칸에도 못 들어가는 카테고리는 "기본 포함" 으로 */
export function isIncludedOnly(part: api.PartCandidate): boolean {
  return SLOT_TYPES_OF_CATEGORY[part.category] === undefined;
}

/** 채우기 — from 칸의 부품을 같은 종류의 열린 빈 칸 count 개에 복제. 순수 */
export function fillSlots(data: ServerConfigEditorData, from: SlotKey, count: number): Installed[] {
  const source = data.installed.find((i) => i.slot === from);
  assert(source !== undefined, `채우기의 시작 칸 ${from} 에 장착된 부품이 없다`);
  const taken = new Set(data.installed.map((i) => i.slot));
  const type  = source.slot.split(':')[0] as SlotType;
  const empty = slotsOf(data).filter((s) => s.type === type && !s.closed && !taken.has(s.key) && canPlace(s, source.part));
  const copies: Installed[] = empty.slice(0, count).map((s) => ({ slot: s.key, part: { ...source.part, serial: null, unit_id: null } }));
  return [...data.installed, ...copies];
}

/** 어느 칸 종류를 먼저 노리나 — 전용 자리가 있으면 그쪽(컨트롤러 → 전용 슬롯, NIC → OCP), 없으면 PCIe. SLOT_TYPES_OF_CATEGORY 를 뒤에서부터 */
function preferredSlotTypes(part: api.PartCandidate): SlotType[] {
  const types = SLOT_TYPES_OF_CATEGORY[part.category];
  return types === undefined ? [] : [...types].reverse();
}

/** 전부 배치 — 부품들을 규칙대로 빈 칸에 앉힌다(순수). 케이지 킷 · 라이저(자리를 여는 것)를 먼저 기본 포함에 넣어 칸을 열고,
 *  CPU → DIMM(소켓마다 고르게 — 덜 찬 뱅크부터, 낮은 번호부터) → 베이(꼴이 맞는 칸만) → 전용 자리 → PCIe 순. 자리 없는 것은 기본 포함으로 */
export function autoPlace(data: ServerConfigEditorData, parts: readonly api.PartCandidate[]): { installed: Installed[]; included: Included[] } {
  let installed = [...data.installed];
  let included  = [...data.included];
  const addIncluded = (part: api.PartCandidate): void => {
    const cur = included.find((i) => i.part.option_part === part.option_part);
    included = cur ? included.map((i) => (i === cur ? { ...i, qty: i.qty + 1 } : i)) : [...included, { part, qty: 1 }];
  };
  const order = (p: api.PartCandidate): number => ({ drive_tray: 0, riser: 0, cpu: 1, memory: 2, ssd: 3, hdd: 3, raid_controller: 4, hba: 4, network_adapter: 5, gpu: 6, power_supply: 7 } as Record<string, number>)[p.category] ?? 9;
  for (const part of [...parts].sort((a, b) => order(a) - order(b))) {
    if (isIncludedOnly(part)) { addIncluded(part); continue; }
    const taken = new Set(installed.map((i) => i.slot));
    const slots = slotsOf({ ...data, installed, included });
    const candidates = slots.filter((s) => !taken.has(s.key) && canPlace(s, part));
    const pick = part.category === 'memory' ? leastFilledBank(candidates, installed) : preferredSlotTypes(part).map((t) => candidates.find((s) => s.type === t)).find((s) => s !== undefined);
    if (pick === undefined) { addIncluded(part); continue; }   // 자리가 다 찼다 — 그래도 버리지 않는다
    installed = [...installed, { slot: pick.key, part }];
  }
  return { installed, included };
}

/** DIMM — 덜 찬 뱅크의 가장 낮은 번호. 'dimm:A3' 의 뱅크는 A */
function leastFilledBank(candidates: Slot[], installed: Installed[]): Slot | undefined {
  const bankOf = (key: string): string => key.slice('dimm:'.length, 'dimm:'.length + 1);
  const filled = new Map<string, number>();
  for (const i of installed) if (i.slot.startsWith('dimm:')) filled.set(bankOf(i.slot), (filled.get(bankOf(i.slot)) ?? 0) + 1);
  return [...candidates].sort((a, b) => (filled.get(bankOf(a.key)) ?? 0) - (filled.get(bankOf(b.key)) ?? 0))[0];
}

// ─── 컴포넌트 ────────────────────────────────────────────────────
export class ServerConfigEditor extends SolaView<ServerConfigEditorData> {
  override events = {   // 순서가 뜻이다 — sola 는 처음 맞는 셀렉터 하나만 부른다. 칸 안의 버튼(채우기 · 빼기)이 칸(@slot)보다 먼저
    click: {
      '@remove':      'handleRemove',
      '@fill':        'handleFill',
      '@open-group':  'handleOpenGroup',
      '@slot':        'handleSlotClick',
      '@include':     'handleInclude',
      '@card':        'handleCardClick',
      '@inc-qty':     'handleIncQty',
      '@layout':      'handleLayout',
      '@place-all':   'handlePlaceAll',
    },
    input: { '@query': 'handleQuery' },
  };

  private dragging: api.PartCandidate | null = null;

  constructor(frame: api.ChassisFrame, parts: api.PartCandidate[]) {
    super();
    this.data = { frame, installed: [], included: [], layout: 'figure', selectedSlot: null, query: '', parts, photoParts: [], baseParts: [] };
  }

  /** 밖(페이지)이 미리 채울 때 — 등록된 사양 고르기 · PO 줄 · 다음 대 */
  setConfig(installed: Installed[], included: Included[]): void {
    this.data = { ...this.data, installed, included, selectedSlot: null };
    this.repaint();
  }

  /** BTO 의 기본 포함 — 지금 앉아 있는 옛 기본 포함을 걷어내고 새것을 규칙대로 앉힌다(autoPlace). CTO 로 바꾸면 [] 로 불러 걷어낸다 */
  setBaseParts(parts: api.PartCandidate[]): void {
    const old = new Set(this.data.baseParts);
    const kept = this.data.installed.filter((i) => !old.has(i.part));
    const { installed, included } = autoPlace({ ...this.data, installed: kept }, parts);
    this.data = { ...this.data, baseParts: parts, installed, included, selectedSlot: null };
    this.changed();
  }

  /** 📷 사진이 읽은 부품을 트레이에 올린다 — 탭이 📷 로 넘어가고 [전부 배치] 가 뜬다 */
  setPhotoParts(parts: api.PartCandidate[]): void {
    this.data = { ...this.data, photoParts: parts, selectedSlot: null };
    this.repaint();
  }

  /** 📷 읽은 부품을 규칙대로 전부 앉힌다 — 페이지가 "등록된 구성과 일치" 를 골랐을 때도 이것 */
  placeAll(): void {
    const { installed, included } = autoPlace(this.data, this.photoPartsLeft());
    this.data = { ...this.data, installed, included, selectedSlot: null };
    this.changed();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`<div class="sce"><div class="sce-body">${this.renderBody()}</div></div>`;
  }

  override onMount(): void { this.wireDnd(); }

  // ===== 그리기 (순수) =====

  private renderBody(): string {
    const d = this.data;
    return `
      <div class="sce-main">
        <div class="sce-toolbar">
          <span class="sce-count">장착 ${d.installed.length} · 기본 포함(자리 없음) ${d.included.reduce((a, i) => a + i.qty, 0)}</span>
          <div class="sce-layouts">
            <button type="button" class="sce-layout${d.layout === 'figure' ? ' active' : ''}" data-action="layout" data-layout="figure">그림</button>
            <button type="button" class="sce-layout${d.layout === 'table' ? ' active' : ''}" data-action="layout" data-layout="table">표</button>
          </div>
        </div>
        ${d.layout === 'figure' ? this.renderFigure() : this.renderTable()}
        ${this.renderIncluded()}
      </div>
      <aside class="sce-parts">${this.renderParts()}</aside>`;
  }

  /** 그림 보기 — 앞면(베이) · 뒷면(PSU · PCIe · OCP) · 보드(CPU · DIMM · 컨트롤러). 칸은 group 으로 묶인다 */
  private renderFigure(): string {
    const slots = slotsOf(this.data);
    const face = (title: string, pick: (s: Slot) => boolean): string => {
      const groups = new Map<string, Slot[]>();
      for (const s of slots.filter(pick)) groups.set(s.group, [...(groups.get(s.group) ?? []), s]);
      const html = [...groups.entries()].map(([g, ss]) => `
        <div class="sce-group${ss.every((s) => s.closed) ? ' closed' : ''}">
          <div class="sce-group-title">${escapeHtml(g)}${ss[0] !== undefined && ss[0].closed ? ` <small>${escapeHtml(ss[0].closedWhy)}</small>${this.renderOpenButton(ss[0])}` : ''}</div>
          <div class="sce-slots sce-slots-${ss[0]?.type ?? ''}">${ss.map((s) => this.renderSlot(s)).join('')}</div>
        </div>`).join('');
      return `<section class="sce-face"><h4>${title}</h4>${html}</section>`;
    };
    return `
      <div class="sce-figure">
        ${face('보드 — CPU · 메모리 · 컨트롤러', (s) => s.type === 'board' || s.type === 'cpu' || s.type === 'dimm' || s.type === 'controller')}
        ${face('앞면 — 드라이브', (s) => s.type === 'bay')}
        ${face('뒷면 — 확장 · PSU', (s) => s.type === 'psu' || s.type === 'pcie' || s.type === 'ocp')}
        <div class="sce-embedded text-muted">내장 — NIC ${escapeHtml(this.data.frame.embedded_nic)} · ${escapeHtml(this.data.frame.bmc)}${this.data.frame.embedded_storage_controller !== null ? ` · ${escapeHtml(this.data.frame.embedded_storage_controller)}` : ''}</div>
      </div>`;
  }

  /** 닫힌 옵션 자리의 「장착」 — 여는 부품(케이지 킷 · 라이저)을 기본 포함에 넣어 칸을 연다. CPU 수 때문에 닫힌 건 버튼 없음 */
  private renderOpenButton(s: Slot): string {
    const pn = this.enablingPartOf(s);
    return pn === null ? '' : ` <button type="button" class="sce-mini sce-open" data-action="open-group" data-pn="${escapeHtml(pn)}" title="${escapeHtml(pn)} 를 기본 포함에 넣고 칸을 연다">장착</button>`;
  }

  private enablingPartOf(s: Slot): string | null {
    if (s.type === 'bay') {
      const bay = this.data.frame.bays.find((b) => s.key.startsWith(`bay:${b.position}-`));
      return bay === undefined || bay.is_default ? null : bay.enabling_parts[0] ?? null;
    }
    if (s.type === 'pcie') {
      const def = this.data.frame.pcie.find((p) => s.key === `pcie:${p.riser}-${p.number}`);
      return def === undefined || def.is_default ? null : def.riser_part;
    }
    return null;
  }

  private renderSlot(s: Slot): string {
    const placed = this.data.installed.find((i) => i.slot === s.key);
    const selected = this.data.selectedSlot === s.key;
    const classes = ['sce-slot', `sce-slot-${s.type}`, placed ? 'filled' : 'empty', placed !== undefined && this.data.baseParts.includes(placed.part) ? 'base' : '', s.closed ? 'closed' : '', selected ? 'selected' : ''].filter(Boolean).join(' ');
    if (s.type === 'board') {
      return `<div class="${classes} board" data-slot="${s.key}" title="메인보드 — 섀시에 붙어 온다. 부품이 아니라 PN · unit 이 없다">메인보드 · 섀시 포함</div>`;
    }
    const bBase = placed !== undefined && this.data.baseParts.includes(placed.part);
    const body = placed
      ? `<span class="sce-pn">${escapeHtml(placed.part.option_part)}</span><span class="sce-sn">${bBase ? '<span class="sce-base">기본 포함</span>' : placed.part.serial === null ? '—' : escapeHtml(placed.part.serial)}</span>
         <span class="sce-slot-actions"><button type="button" class="sce-mini" data-action="fill" data-slot="${s.key}" title="같은 것으로 나머지 빈 칸을 다 채운다">채우기</button><button type="button" class="sce-mini" data-action="remove" data-slot="${s.key}" title="빼기">✕</button></span>`
      : `<span class="sce-slot-label">${escapeHtml(s.label)}</span>`;
    return `<div class="${classes}" data-action="slot" data-slot="${s.key}" data-type="${s.type}" title="${escapeHtml(s.closed ? s.closedWhy : s.group + ' ' + s.label)}">${body}</div>`;
  }

  /** 표 보기 — installed 를 (종류 · PN) 으로 묶어 수량 */
  private renderTable(): string {
    const rows = new Map<string, { type: SlotType; part: api.PartCandidate; qty: number; serials: number }>();
    for (const i of this.data.installed) {
      const type = i.slot.split(':')[0] as SlotType;
      const k = `${type}|${i.part.option_part}`;
      const cur = rows.get(k) ?? { type, part: i.part, qty: 0, serials: 0 };
      rows.set(k, { ...cur, qty: cur.qty + 1, serials: cur.serials + (i.part.serial === null ? 0 : 1) });
    }
    const body = [...rows.values()].map((r) => `
      <tr><td>${SLOT_LABEL[r.type]}</td><td class="mono">${escapeHtml(r.part.option_part)}</td><td>${escapeHtml(r.part.description)}</td><td class="num">${r.qty}</td><td class="num text-muted">${r.serials}/${r.qty}</td></tr>`).join('');
    return `
      <table class="sce-table">
        <thead><tr><th>자리</th><th>PN</th><th>설명</th><th class="num">수량</th><th class="num">시리얼</th></tr></thead>
        <tbody>${body || '<tr><td colspan="5" class="text-muted">장착된 것이 없습니다 — 그림에서 끌어 놓거나 카드를 클릭하세요</td></tr>'}</tbody>
      </table>`;
  }

  private renderIncluded(): string {
    const rows = this.data.included.map((i, n) => `
      <tr><td class="mono">${escapeHtml(i.part.option_part)}</td><td>${escapeHtml(i.part.description)}</td>
          <td class="num"><button type="button" class="sce-mini" data-action="inc-qty" data-index="${n}" data-delta="-1">−</button> ${i.qty} <button type="button" class="sce-mini" data-action="inc-qty" data-index="${n}" data-delta="1">+</button></td></tr>`).join('');
    return `
      <section class="sce-included">
        <h4>기본 포함 <small class="text-muted">자리 없는 부품만 — 팬 · 레일킷 · 케이블 · 케이지 킷 · 라이저. CPU 처럼 자리가 있는 건 칸에</small></h4>
        <table class="sce-table"><tbody>${rows || '<tr><td class="text-muted" colspan="3">없음 — 카드의 「기본 포함으로」</td></tr>'}</tbody></table>
      </section>`;
  }

  /** 고르기 — 한 목록. 📷 읽은 것(아직 안 앉은 것) → 재고 unit → 새 unit(카탈로그) 순, 검색 칸이 치는 대로 거른다. 칸을 골랐으면 그 칸에 맞는 것만. 📷 가 남았으면 [전부 배치] */
  private renderParts(): string {
    const d = this.data;
    const taken = new Set(d.installed.map((i) => i.part.unit_id).filter((u): u is number => u !== null));
    const selected = d.selectedSlot === null ? null : slotsOf(d).find((s) => s.key === d.selectedSlot) ?? null;
    const q = d.query.trim().toUpperCase();
    const photoLeft = this.photoPartsLeft();
    const source = [...photoLeft, ...d.parts.filter((p) => p.unit_id !== null), ...d.parts.filter((p) => p.unit_id === null)];
    const cards = source
      .filter((p) => p.unit_id === null || !taken.has(p.unit_id))
      .filter((p) => q === '' || p.option_part.toUpperCase().includes(q) || p.description.toUpperCase().includes(q))
      .filter((p) => selected === null || canPlace(selected, p));
    const srcOf = (p: api.PartCandidate): string => photoLeft.includes(p) ? '<span class="sce-src photo">📷 라벨</span>' : p.unit_id !== null ? `<span class="sce-src stock">재고 #${p.unit_id}</span>` : '<span class="sce-src new">새 unit</span>';
    return `
      <input class="sce-query" data-action="query" placeholder="PN · 설명으로 찾기 — 📷 읽은 것 · 재고 · 카탈로그 한 번에" value="${escapeHtml(d.query)}" />
      ${photoLeft.length > 0 ? `<div class="sce-place-all"><button type="button" class="btn-primary" data-action="place-all">📷 읽은 ${photoLeft.length}장 전부 배치</button><div class="sce-hint text-muted">CPU → DIMM 고르게 → 베이 → 컨트롤러 · NIC → PSU 순으로 규칙대로. 케이지 킷 · 라이저는 기본 포함으로 가며 칸을 엽니다</div></div>` : ''}
      <div class="sce-hint text-muted">${selected === null ? '칸을 클릭하면 맞는 것만 남습니다 · 카드를 칸에 끌어다 놓아도 됩니다' : `${escapeHtml(selected.group)} ${escapeHtml(selected.label)} 에 맞는 것 — 카드를 클릭하면 들어갑니다`}</div>
      <div class="sce-cards">
        ${cards.map((p, n) => `
          <div class="sce-card${photoLeft.includes(p) ? ' photo' : ''}" draggable="true" data-action="card" data-card="${this.cardIndexOf(p)}" data-n="${n}">
            <span class="sce-card-head"><span class="sce-pn">${escapeHtml(p.option_part)}</span>${srcOf(p)}</span>
            <span class="sce-desc">${escapeHtml(p.description)}</span>
            ${p.serial === null ? '' : `<span class="sce-meta text-muted">S/N ${escapeHtml(p.serial)}</span>`}
            ${isIncludedOnly(p) ? `<button type="button" class="sce-mini" data-action="include" data-card="${this.cardIndexOf(p)}">기본 포함으로</button>` : ''}
          </div>`).join('') || '<div class="text-muted sce-empty">맞는 것이 없습니다</div>'}
      </div>`;
  }

  /** 📷 읽은 부품 중 아직 자리에 안 앉은 것 — 같은 개체(참조)로 센다. 라벨 한 장 = 한 장이라 PN 이 같아도 따로 */
  private photoPartsLeft(): api.PartCandidate[] {
    const placed = new Set<api.PartCandidate>([...this.data.installed.map((i) => i.part), ...this.data.included.map((i) => i.part)]);
    return this.data.photoParts.filter((p) => !placed.has(p));
  }

  /** 카드 번호 — parts 는 양수, 📷 부품은 음수(-1 부터)로 가른다 */
  private cardIndexOf(p: api.PartCandidate): number {
    const n = this.data.parts.indexOf(p);
    if (n >= 0) return n;
    const m = this.data.photoParts.indexOf(p);
    assert(m >= 0, `카드가 parts · photoParts 어디에도 없다: ${p.option_part}`);
    return -1 - m;
  }

  // ===== 이벤트 =====

  handleSlotClick(_e: Event, el: HTMLElement): void {
    const key = el.dataset.slot as SlotKey | undefined;
    assert(key !== undefined, '칸에 data-slot 이 없다');
    this.data.selectedSlot = this.data.selectedSlot === key ? null : key;
    this.repaint();
  }

  /** 카드 클릭 — 칸이 골라져 있으면 거기에 놓는다 */
  handleCardClick(_e: Event, el: HTMLElement): void {
    const part = this.partOf(el);
    if (this.data.selectedSlot === null) return;
    this.place(this.data.selectedSlot, part);
  }

  handleRemove(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    const key = el.dataset.slot as SlotKey;
    this.data.installed = this.data.installed.filter((i) => i.slot !== key);
    this.changed();
  }

  handleFill(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    const key = el.dataset.slot as SlotKey;
    this.data.installed = fillSlots(this.data, key, Number.MAX_SAFE_INTEGER);   // 나머지 전부 — 24 DIMM 을 하나씩 놓게 하지 않는다
    this.changed();
  }

  handleInclude(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    const part = this.partOf(el);
    const cur = this.data.included.find((i) => i.part.option_part === part.option_part);
    this.data.included = cur
      ? this.data.included.map((i) => (i === cur ? { ...i, qty: i.qty + 1 } : i))
      : [...this.data.included, { part, qty: 1 }];
    this.changed();
  }

  handleIncQty(_e: Event, el: HTMLElement): void {
    const n = Number(el.dataset.index); const delta = Number(el.dataset.delta);
    this.data.included = this.data.included.map((i, k) => (k === n ? { ...i, qty: i.qty + delta } : i)).filter((i) => i.qty > 0);
    this.changed();
  }

  handleLayout(_e: Event, el: HTMLElement): void {
    const layout = el.dataset.layout;
    assert(layout === 'figure' || layout === 'table', `layout 은 figure · table 중 하나 — ${layout}`);
    this.data.layout = layout;
    this.repaint();
  }

  handlePlaceAll(): void { this.placeAll(); }

  /** 「장착」 — 여는 부품을 기본 포함에 한 장 넣는다(이미 있으면 그대로). 카드 목록에 있으면 그 카드, 없으면 PN 만으로 한 장 */
  handleOpenGroup(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    const pn = el.dataset.pn;
    assert(pn !== undefined, '장착 버튼에 data-pn 이 없다');
    if (this.data.included.some((i) => i.part.option_part === pn)) return;
    const known = this.data.parts.find((p) => p.option_part === pn);
    const part: api.PartCandidate = known ?? { vendor: 'HPE', option_part: pn, category: 'drive_tray', description: `옵션 자리를 여는 킷 ${pn}`, serial: null, unit_id: null };
    this.data.included = [...this.data.included, { part, qty: 1 }];
    this.changed();
  }

  handleQuery(_e: Event, el: HTMLElement): void {
    this.data.query = (el as HTMLInputElement).value;
    this.refreshRegion('.sce-cards', () => this.renderParts().split('<div class="sce-cards">')[1]?.replace(/<\/div>\s*$/, '') ?? '');
    this.wireDnd();
  }

  // ===== 안쪽 =====

  private partOf(el: HTMLElement): api.PartCandidate {
    const n = Number(el.dataset.card);
    const part = n >= 0 ? this.data.parts[n] : this.data.photoParts[-1 - n];
    assert(part !== undefined, `data-card ${el.dataset.card} 가 parts · photoParts 밖이다`);
    return part;
  }

  private place(key: SlotKey, part: api.PartCandidate): void {
    const slot = slotsOf(this.data).find((s) => s.key === key);
    assert(slot !== undefined, `놓을 칸 ${key} 가 틀에 없다`);
    if (!canPlace(slot, part)) return;
    this.data.installed = [...this.data.installed.filter((i) => i.slot !== key), { slot: key, part }];
    this.data.selectedSlot = null;
    this.changed();
  }

  private changed(): void {
    this.repaint();
    this.emit('config:change', { installed: this.data.installed, included: this.data.included });
  }

  private repaint(): void {
    this.refreshRegion('.sce-body', () => this.renderBody());
    this.wireDnd();
  }

  /** 드래그 — 카드(dragstart) → 칸(drop). 맞는 칸만 drop-ok. IssueListView 의 꼴 */
  private wireDnd(): void {
    const root = this._rootElement; if (!root) return;
    root.querySelectorAll<HTMLElement>('.sce-card').forEach((card) => {
      card.addEventListener('dragstart', (e) => {
        this.dragging = this.partOf(card);
        card.classList.add('dragging');
        (e as DragEvent).dataTransfer?.setData('text/plain', card.dataset.card ?? '');
        const slots = slotsOf(this.data);
        root.querySelectorAll<HTMLElement>('.sce-slot').forEach((cell) => {
          const s = slots.find((x) => x.key === cell.dataset.slot);
          if (s !== undefined && this.dragging !== null && canPlace(s, this.dragging)) cell.classList.add('drop-ok'); else cell.classList.add('drop-dim');
        });
      });
      card.addEventListener('dragend', () => { card.classList.remove('dragging'); this.dragging = null; root.querySelectorAll('.sce-slot').forEach((c) => c.classList.remove('drop-ok', 'drop-dim')); });
    });
    root.querySelectorAll<HTMLElement>('.sce-slot').forEach((cell) => {
      cell.addEventListener('dragover', (e) => { if (cell.classList.contains('drop-ok')) e.preventDefault(); });
      cell.addEventListener('drop', (e) => {
        e.preventDefault();
        if (!cell.classList.contains('drop-ok') || this.dragging === null) return;
        this.place(cell.dataset.slot as SlotKey, this.dragging);
      });
    });
  }
}
